import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getDomains } from '../../service/api/domainService';
import { getDomainValidators, createDomainValidator, deleteDomainValidator } from '../../service/api/validation-service';
import SearchableSelect from '../../SearchableSelect';
import { getAccountss } from '../../service/api/Order';
import BootstrapModal from '../../admin/Validateurs/ConfirmationModal';

const DomainValidatorsManager = () => {
  const queryClient = useQueryClient();
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [validatorToDelete, setValidatorToDelete] = useState(null);

  // Chargement des domaines
  const { data: domains = [], isLoading: loadingDomains } = useQuery({
    queryKey: ['domains'],
    queryFn: getDomains,
  });

  // Chargement des comptes
  const { data: accountsData, isLoading: loadingAccounts } = useQuery({
    queryKey: ['accounts', selectedDomain?.id],
    queryFn: () => {
      if (!selectedDomain?.id) return [];
      return getAccountss(selectedDomain.id);
    },
    enabled: !!selectedDomain?.id,
  });

  // S'assurer que accounts est toujours un tableau
  const accounts = Array.isArray(accountsData) ? accountsData : 
                 (accountsData?.items && Array.isArray(accountsData.items)) ? accountsData.items : [];

  // Chargement des validateurs
  const { data: validatorsData, isLoading: loadingValidators } = useQuery({
    queryKey: ['domain-validators', selectedDomain?.id],
    queryFn: () => {
      if (!selectedDomain?.id) return [];
      return getDomainValidators(selectedDomain.id);
    },
    enabled: !!selectedDomain?.id,
  });

  const validators = Array.isArray(validatorsData) ? validatorsData : 
                   (validatorsData?.items && Array.isArray(validatorsData.items)) ? validatorsData.items : [];

  // Mutation pour ajouter un validateur
  const addValidatorMutation = useMutation({
    mutationFn: createDomainValidator,
    onSuccess: () => {
      toast.success('✅ Validateur ajouté avec succès');
      setSelectedAccount(null);
      queryClient.invalidateQueries(['domain-validators', selectedDomain?.id]);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Erreur lors de l\'ajout du validateur';
      toast.error(`❌ ${errorMessage}`);
    },
  });

  // Mutation pour supprimer un validateur
  const deleteValidatorMutation = useMutation({
    mutationFn: deleteDomainValidator,
    onSuccess: () => {
      toast.success('✅ Validateur retiré avec succès');
      setShowDeleteModal(false);
      setValidatorToDelete(null);
      queryClient.invalidateQueries(['domain-validators', selectedDomain?.id]);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression';
      toast.error(`❌ ${errorMessage}`);
      setShowDeleteModal(false);
      setValidatorToDelete(null);
    },
  });

  const handleAddValidator = () => {
    if (!selectedDomain || !selectedAccount) {
      toast.warning('⚠️ Veuillez sélectionner un domaine et un compte');
      return;
    }

    addValidatorMutation.mutate({
      domainId: selectedDomain.id,
      accountId: selectedAccount.id,
      validationLevel: 1,
    });
  };

  const openDeleteModal = (validator) => {
    console.log('Opening delete modal for validator:', validator);
    setValidatorToDelete(validator);
    setShowDeleteModal(true);
    console.log('showDeleteModal after set:', true);
  };

// Ajoutez ce useEffect pour debugger l'état du modal
  useEffect(() => {
    console.log('Modal state changed - showDeleteModal:', showDeleteModal);
    console.log('validatorToDelete:', validatorToDelete);
  }, [showDeleteModal, validatorToDelete]);

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setValidatorToDelete(null);
  };

  const confirmDelete = () => {
    if (validatorToDelete) {
      deleteValidatorMutation.mutate({ 
        domainId: validatorToDelete.domainId, 
        accountId: validatorToDelete.accountId 
      });
    }
  };

  return (
    <div>
      {/* Modal de confirmation de suppression */}
      {showDeleteModal && validatorToDelete && (
        <BootstrapModal
          show={showDeleteModal}
          onHide={closeDeleteModal}
          message={`Êtes-vous sûr de vouloir retirer ${validatorToDelete?.accountName || 'ce validateur'} du domaine ?`}
          onConfirm={confirmDelete}
          isLoading={deleteValidatorMutation.isLoading}
        />
      )}

      {/* Sélection du Domaine et du Compte */}
      <div className="row mb-4">
        <div className="col-md-6">
          <label className="form-label">
            Domaine <span className="text-danger">*</span>
          </label>
          <SearchableSelect
            options={domains}
            value={selectedDomain}
            onChange={setSelectedDomain}
            valueKey="id"
            labelKey="name"
            searchKeys={['name', 'email']}
            placeholder="Sélectionnez un domaine"
            className={!selectedDomain ? 'form-select is-invalid' : 'form-select'}
          />
          {!selectedDomain && (
            <div className="invalid-feedback">Veuillez sélectionner un domaine</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Compte <span className="text-danger">*</span>
          </label>
          <SearchableSelect
            options={accounts}
            value={selectedAccount}
            onChange={setSelectedAccount}
            valueKey="id"
            labelKey={(account) => 
              `${account.firstName || ''} ${account.lastName || ''} ${account.companyName || ''}`.trim() || 
              account.email
            }
            searchKeys={['firstName', 'lastName', 'email', 'companyName']}
            placeholder={selectedDomain ? "Sélectionnez un compte" : "Sélectionnez d'abord un domaine"}
            isDisabled={!selectedDomain}
            isLoading={loadingAccounts}
            className={!selectedAccount ? 'form-select is-invalid' : 'form-select'}
          />
          {!selectedAccount && selectedDomain && (
            <div className="invalid-feedback">Veuillez sélectionner un compte</div>
          )}
        </div>
      </div>

      {/* Bouton d'ajout */}
      <div className="mb-4">
        <button
          className="btn btn-primary"
          onClick={handleAddValidator}
          disabled={!selectedDomain || !selectedAccount || addValidatorMutation.isLoading}
        >
          {addValidatorMutation.isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Ajout en cours...
            </>
          ) : (
            <>
              <i className="bi bi-plus-circle me-2"></i>
              Ajouter comme validateur
            </>
          )}
        </button>
      </div>

      {/* Liste des validateurs */}
      {selectedDomain && (
        <div>
          <h5 className="mb-3">Validateurs du domaine "{selectedDomain.name}"</h5>
          
          {loadingValidators ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="text-muted mt-2">Chargement des validateurs...</p>
            </div>
          ) : validators.length === 0 ? (
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              Aucun validateur configuré pour ce domaine
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Niveau</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {validators.map((validator) => (
                    <tr key={validator.id}>
                      <td>
                        {validator.accountName || 
                         `${validator.account?.firstName || ''} ${validator.account?.lastName || ''}`.trim() ||
                         'Nom non disponible'}
                      </td>
                      <td>{validator.accountEmail || validator.account?.email || 'Email non disponible'}</td>
                      <td>
                        <span className="badge bg-secondary">Niveau {validator.validationLevel}</span>
                      </td>
                      <td>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => openDeleteModal(validator)}
                          disabled={deleteValidatorMutation.isLoading}
                          title="Retirer le validateur"
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Messages de chargement pour les mutations */}
      {addValidatorMutation.isLoading && (
        <div className="alert alert-info mt-3">
          <span className="spinner-border spinner-border-sm me-2"></span>
          Ajout du validateur en cours...
        </div>
      )}

      {deleteValidatorMutation.isLoading && (
        <div className="alert alert-info mt-3">
          <span className="spinner-border spinner-border-sm me-2"></span>
          Suppression du validateur en cours...
        </div>
      )}
    </div>
  );
};

export default DomainValidatorsManager;